"use client";

import { useEffect } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import type { PortalDashboard, PortalEvent, PublicClientPortal } from "../lib/clientPortalApi";
import { hasEventTickets, isAdmissionEvent } from "../lib/eventParticipation";
import { portalEventRegistrationPath } from "../lib/portalEventRegistration";
import { usePortalTranslations } from "../i18n/client";
import { usePortalPreferences } from "./PortalPreferences";
import styles from "./PortalSurface.module.css";
import eventStyles from "./PortalEvents.module.css";

export function PortalEvents({portal, dashboard}: {portal: PublicClientPortal; dashboard: PortalDashboard}) {
  const t = usePortalTranslations();
  const router = useRouter();
  const events = dashboard.events ?? [];
  useEffect(() => {
    if (!events.length) return;
    const refresh = () => { if (document.visibilityState === "visible") router.refresh(); };
    const timer = window.setInterval(refresh, 30_000);
    window.addEventListener("focus", refresh);
    return () => { window.clearInterval(timer); window.removeEventListener("focus", refresh); };
  }, [events.length, router]);
  if (!events.length) return null;
  return <section className={`${styles.section} ${styles.catalogSection}`} id="events" data-portal-home-section>
    <div className={`${styles.sectionHeading} ${styles.catalogHeading}`}><h2>{t("events.title")}</h2></div>
    <div className={styles.sectionContent}><div className={eventStyles.grid}>
      {events.map((item) => <EventCard key={item.event.uuid} item={item} portal={portal} />)}
    </div></div>
  </section>;
}

function EventCard({item, portal}: {item: PortalEvent; portal: PublicClientPortal}) {
  const t = usePortalTranslations();
  const { locale } = usePortalPreferences();
  const event = item.event;
  const admission = isAdmissionEvent(event);
  const textGroup = hasEventTickets(event) ? "tickets" : "events";
  const date = (value: string) => new Intl.DateTimeFormat(locale, {dateStyle: "medium", timeStyle: "short", timeZone: event.timezone}).format(new Date(value));

  return <article className={eventStyles.card}>
    <header><h3>{event.name}</h3>{event.description ? <p>{event.description}</p> : null}
      {event.location ? <p>{event.location}</p> : null}{event.organizer ? <small>{event.organizer}</small> : null}</header>
    <div className={eventStyles.occurrences}>{event.occurrences.filter((entry) => !entry.cancelled).map((entry) => {
      const registered = item.registeredOccurrenceUuids.includes(entry.uuid);
      const past = new Date(entry.startAt).getTime() <= Date.now();
      const full = entry.remaining !== null && entry.remaining < 1;
      const registrationHref = portalEventRegistrationPath(portal.slug, event.uuid, entry.uuid);
      return <div key={entry.uuid} className={eventStyles.occurrence}>
        <div><div className={eventStyles.timeRange}><time dateTime={entry.startAt}>{date(entry.startAt)}</time><span aria-hidden="true">–</span><time dateTime={entry.endAt}>{date(entry.endAt)}</time></div><p>{entry.capacity === null ? t("events.unlimited") : t("events.capacity", {occupied: entry.occupied, capacity: entry.capacity})}</p></div>
        <div className={eventStyles.occurrenceActions}>
          {registered ? <span className={styles.badge}>{t(`${textGroup}.registered`)}</span> : null}
          {!registered || admission ? full || past ? <span className={styles.badge}>{t(past ? "events.started" : "events.full")}</span> : item.canRegister ? <Link className="primary-button" href={registrationHref}>{t(admission && registered ? "tickets.registerMore" : `${textGroup}.register`)}</Link> : portal.loginEnabled ? <Link className={styles.textLink} href={registrationHref}>{t("events.login")}</Link> : <span>{t(`${textGroup}.unavailable`)}</span> : null}
        </div>
      </div>;
    })}</div>
  </article>;
}
